/**
 * GitHub Monitor Page
 * View GitHub Actions workflow runs and their statuses (Admin only)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Stack,
  Flex,
  Button,
  useTheme,
  spacing,
  textStyles,
  fontSize,
} from '@fti/design-system';
import {
  listWorkflowRuns,
  getWorkflowRunDetails,
  getWorkflowJobs,
  isRunning,
  isSuccessful,
  isFailed,
} from '../api/github';
import type { GitHubWorkflowRun, GitHubWorkflowJob } from '../api/types/echo';

type StatusFilter = 'all' | 'in_progress' | 'completed' | 'success' | 'failure';

/**
 * Get emoji indicator for workflow status
 */
function getStatusEmoji(run: GitHubWorkflowRun): string {
  if (run.status === 'queued') return '...';
  if (isRunning(run)) return '~';
  if (isSuccessful(run)) return 'OK';
  if (isFailed(run)) return 'X';
  return '?';
}

/**
 * Get emoji for job status
 */
function getJobEmoji(job: GitHubWorkflowJob): string {
  if (job.status === 'completed') {
    if (job.conclusion === 'success') return 'OK';
    if (job.conclusion === 'failure' || job.conclusion === 'cancelled') return 'X';
    return '?';
  }
  if (job.status === 'in_progress' || job.status === 'queued') return '~';
  return '?';
}

/**
 * Individual workflow run card with expandable details
 */
function WorkflowRunCard({ run: initialRun }: { run: GitHubWorkflowRun }) {
  const { theme } = useTheme();
  const [expanded, setExpanded] = useState(isRunning(initialRun));
  const [showJobs, setShowJobs] = useState(false);
  const [run, setRun] = useState(initialRun);
  const [jobs, setJobs] = useState<GitHubWorkflowJob[]>([]);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [loadingJobs, setLoadingJobs] = useState(false);

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const successColor = theme.colors.semantic.success.default;
  const errorColor = theme.colors.semantic.error.default;
  const infoColor = theme.colors.semantic.info.default;
  const borderColor = theme.colors.border.default;

  // Fetch detailed run info when expanded
  useEffect(() => {
    if (expanded && !run.inputs) {
      getWorkflowRunDetails(run.id).then(({ run: detailed }) => {
        if (detailed) {
          setRun(detailed);
        }
      });
    }
  }, [expanded, run.id, run.inputs]);

  // Fetch jobs when toggled
  useEffect(() => {
    if (showJobs && jobs.length === 0 && !loadingJobs) {
      setLoadingJobs(true);
      getWorkflowJobs(run.id).then(({ jobs: fetchedJobs, error }) => {
        setJobs(fetchedJobs);
        setJobsError(error);
        setLoadingJobs(false);
      });
    }
  }, [showJobs, run.id, jobs.length, loadingJobs]);

  const statusEmoji = getStatusEmoji(run);
  const statusText = run.conclusion
    ? run.conclusion.charAt(0).toUpperCase() + run.conclusion.slice(1)
    : run.status.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const getStatusColor = () => {
    if (isSuccessful(run)) return successColor;
    if (isFailed(run)) return errorColor;
    if (isRunning(run)) return infoColor;
    return mutedColor;
  };

  const title = run.display_title || `Run #${run.run_number}`;
  const runDate = new Date(run.created_at).toLocaleString();
  const inputs = run.inputs || {};

  return (
    <Card variant="outlined">
      <Card.Body>
        <Stack gap="sm">
          {/* Header - clickable to expand */}
          <Flex
            justify="between"
            align="center"
            style={{ cursor: 'pointer' }}
            onClick={() => setExpanded(!expanded)}
          >
            <Flex align="center" gap="sm">
              <span
                style={{
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  color: getStatusColor(),
                }}
              >
                [{statusEmoji}]
              </span>
              <div>
                <div style={{ fontWeight: 500, color: textColor, fontSize: fontSize.sm }}>
                  {title}
                </div>
                <div style={{ fontSize: fontSize.xs, color: mutedColor }}>
                  {runDate}
                </div>
              </div>
            </Flex>
            <span style={{ color: mutedColor }}>{expanded ? '[-]' : '[+]'}</span>
          </Flex>

          {/* Expanded details */}
          {expanded && (
            <Stack gap="md" style={{ marginTop: spacing.sm }}>
              {/* Status and metadata */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: spacing.md,
                  padding: spacing.sm,
                  background: theme.colors.background.paper,
                  borderRadius: '4px',
                }}
              >
                <div>
                  <div style={{ fontSize: fontSize.xs, color: mutedColor }}>Status</div>
                  <div style={{ fontSize: fontSize.sm, color: getStatusColor(), fontWeight: 500 }}>
                    {statusEmoji} {statusText}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: fontSize.xs, color: mutedColor }}>Streamer</div>
                  <div style={{ fontSize: fontSize.sm, color: textColor }}>
                    {inputs.streamerName || 'N/A'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: fontSize.xs, color: mutedColor }}>Stream Date</div>
                  <div style={{ fontSize: fontSize.sm, color: textColor }}>
                    {inputs.date || 'N/A'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: fontSize.xs, color: mutedColor }}>Triggered by</div>
                  <div style={{ fontSize: fontSize.sm, color: textColor }}>
                    {run.actor || 'unknown'}
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <Flex gap="lg" wrap="wrap">
                <div>
                  <span style={{ fontSize: fontSize.xs, color: mutedColor }}>Started: </span>
                  <span style={{ fontSize: fontSize.xs, color: textColor }}>
                    {new Date(run.created_at).toLocaleString()}
                  </span>
                </div>
                {run.updated_at && (
                  <div>
                    <span style={{ fontSize: fontSize.xs, color: mutedColor }}>Updated: </span>
                    <span style={{ fontSize: fontSize.xs, color: textColor }}>
                      {new Date(run.updated_at).toLocaleString()}
                    </span>
                  </div>
                )}
              </Flex>

              {/* VOD URL */}
              {inputs.url && (
                <div>
                  <span style={{ fontSize: fontSize.xs, color: mutedColor }}>VOD URL: </span>
                  <a
                    href={inputs.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: fontSize.xs, color: theme.colors.primary[500] }}
                  >
                    {inputs.url}
                  </a>
                </div>
              )}

              {/* Email recipients */}
              {inputs.recipients && (
                <div>
                  <span style={{ fontSize: fontSize.xs, color: mutedColor }}>Email Recipients: </span>
                  <span style={{ fontSize: fontSize.xs, color: textColor }}>{inputs.recipients}</span>
                </div>
              )}

              {/* Actions */}
              <Flex gap="sm" wrap="wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(run.html_url, '_blank')}
                >
                  View on GitHub
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowJobs(!showJobs)}
                >
                  {showJobs ? 'Hide Jobs' : 'Show Jobs'}
                </Button>
              </Flex>

              {/* Jobs */}
              {showJobs && (
                <div
                  style={{
                    padding: spacing.sm,
                    background: theme.colors.background.elevated,
                    borderRadius: '4px',
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  <div style={{ fontSize: fontSize.xs, fontWeight: 600, color: textColor, marginBottom: spacing.xs }}>
                    Jobs
                  </div>
                  {loadingJobs && (
                    <div style={{ fontSize: fontSize.xs, color: mutedColor }}>Loading jobs...</div>
                  )}
                  {jobsError && (
                    <div style={{ fontSize: fontSize.xs, color: errorColor }}>{jobsError}</div>
                  )}
                  {!loadingJobs && jobs.length === 0 && !jobsError && (
                    <div style={{ fontSize: fontSize.xs, color: mutedColor }}>No jobs found.</div>
                  )}
                  {jobs.map((job) => (
                    <div key={job.id} style={{ marginBottom: spacing.xs }}>
                      <div style={{ fontSize: fontSize.xs, color: textColor }}>
                        <span
                          style={{
                            fontFamily: 'monospace',
                            color:
                              job.conclusion === 'success'
                                ? successColor
                                : job.conclusion === 'failure'
                                ? errorColor
                                : infoColor,
                          }}
                        >
                          [{getJobEmoji(job)}]
                        </span>{' '}
                        <strong>{job.name}</strong>:{' '}
                        {(job.conclusion || job.status).replace('_', ' ')}
                      </div>
                      {/* Steps */}
                      {job.steps.length > 0 && (
                        <div style={{ marginLeft: spacing.md, marginTop: spacing.xs }}>
                          {job.steps.map((step) => {
                            let stepIcon = 'o';
                            if (step.conclusion === 'success') stepIcon = '+';
                            else if (step.conclusion === 'failure') stepIcon = 'x';
                            else if (step.status === 'in_progress') stepIcon = '>';

                            return (
                              <div
                                key={step.number}
                                style={{
                                  fontSize: fontSize.xs,
                                  color: mutedColor,
                                  fontFamily: 'monospace',
                                }}
                              >
                                {stepIcon} {step.name}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Stack>
          )}
        </Stack>
      </Card.Body>
    </Card>
  );
}

/**
 * GitHub Monitor Page Component
 */
export function GitHubMonitorPage() {
  const { theme } = useTheme();
  const [runs, setRuns] = useState<GitHubWorkflowRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Map filter to API status parameter
    // success/failure need to fetch completed runs then filter client-side
    let apiStatus: 'queued' | 'in_progress' | 'completed' | undefined;
    if (statusFilter === 'all') {
      apiStatus = undefined;
    } else if (statusFilter === 'success' || statusFilter === 'failure') {
      apiStatus = 'completed';
    } else {
      apiStatus = statusFilter;
    }

    const { runs: fetchedRuns, error: fetchError } = await listWorkflowRuns(30, apiStatus);

    // Client-side filter for success/failure
    let filteredRuns = fetchedRuns;
    if (statusFilter === 'success') {
      filteredRuns = fetchedRuns.filter(r => r.conclusion === 'success');
    } else if (statusFilter === 'failure') {
      filteredRuns = fetchedRuns.filter(r => r.conclusion === 'failure' || r.conclusion === 'cancelled');
    }

    setRuns(filteredRuns);
    setError(fetchError);
    setLoading(false);
  }, [statusFilter]);

  // Initial fetch and refetch on filter change
  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  // Auto-refresh when workflows are running
  useEffect(() => {
    const runningCount = runs.filter((r) => isRunning(r)).length;
    if (runningCount > 0) {
      const timer = setTimeout(() => {
        fetchRuns();
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [runs, fetchRuns]);

  const runningCount = runs.filter((r) => isRunning(r)).length;

  return (
    <Stack gap="lg">
      {/* Page Header */}
      <div>
        <h1 style={{ margin: 0, ...textStyles.h3, color: textColor }}>GitHub Monitor</h1>
        <p style={{ margin: 0, marginTop: spacing.xs, color: mutedColor }}>
          View all GitHub Actions workflow runs and their statuses
        </p>
      </div>

      {/* Controls */}
      <Card variant="outlined">
        <Card.Body>
          <Flex justify="between" align="center" wrap="wrap" gap="md">
            <Flex align="center" gap="sm">
              <span style={{ fontSize: fontSize.sm, color: textColor, fontWeight: 500 }}>
                Status:
              </span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: `1px solid ${theme.colors.border.default}`,
                  backgroundColor: '#fff',
                  color: '#333',
                  fontSize: '14px',
                  cursor: 'pointer',
                  minWidth: '140px',
                }}
              >
                <option value="all">All Statuses</option>
                <option value="in_progress">Running</option>
                <option value="completed">Completed (All)</option>
                <option value="success">Completed (Success)</option>
                <option value="failure">Completed (Failed)</option>
              </select>
            </Flex>

            <Flex align="center" gap="sm">
              <span style={{ fontSize: fontSize.sm, color: mutedColor }}>
                {runs.length} run{runs.length !== 1 ? 's' : ''}
              </span>
              <Button variant="outline" size="sm" onClick={fetchRuns} disabled={loading}>
                Refresh
              </Button>
            </Flex>
          </Flex>
        </Card.Body>
      </Card>

      {/* Auto-refresh notice */}
      {runningCount > 0 && (
        <div
          style={{
            padding: spacing.sm,
            background: `${theme.colors.semantic.info.light}20`,
            border: `1px solid ${theme.colors.semantic.info.light}`,
            borderRadius: '6px',
            fontSize: fontSize.sm,
            color: theme.colors.semantic.info.default,
          }}
        >
          {runningCount} workflow{runningCount !== 1 ? 's' : ''} currently running. Page will
          auto-refresh in 10 seconds.
        </div>
      )}

      {/* Loading state */}
      {loading && runs.length === 0 && (
        <Card variant="outlined">
          <Card.Body>
            <div style={{ textAlign: 'center', color: mutedColor, padding: spacing.lg }}>
              Loading workflow runs...
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Error state */}
      {error && (
        <Card variant="outlined">
          <Card.Body>
            <div style={{ color: theme.colors.semantic.error.default }}>{error}</div>
          </Card.Body>
        </Card>
      )}

      {/* Empty state */}
      {!loading && !error && runs.length === 0 && (
        <Card variant="outlined">
          <Card.Body>
            <div style={{ textAlign: 'center', color: mutedColor, padding: spacing.lg }}>
              No workflow runs found.
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Workflow runs list */}
      <Stack gap="sm">
        {runs.map((run) => (
          <WorkflowRunCard key={run.id} run={run} />
        ))}
      </Stack>
    </Stack>
  );
}

export default GitHubMonitorPage;
